// The office's view of an operation's checklist (docs/op-checklists-plan.md): the Checklist section
// of the op panel (#Update, opPanel.js) — who ticked what on each engine, readings, items taken off,
// why items were left open — with Add item, take off / put back, and Change list (another saved
// list, or none), plus the meter on Work Orders op rows. The same rules and writes as the techs app
// (checklistCore.js, checklistData.js): the list is applied the first time the op is opened here too.

import { getDB, updateTimestamps, escapeHTML, toastMessage } from './utilities';
import { patchHTML } from './liveView';
import { currentUserName } from './haulLaunchShared';
import * as C from './checklistCore';
import * as D from './checklistData';

const esc = v => escapeHTML(v == null ? '' : String(v));
const view = { container: null, op: null, boat: null };
const ui = { opId: null, adding: false, changing: false, removing: null, find: '', draft: { name: '', kind: 'check', unit: 'h', scope: 'engine', keep: true } };
// Adding an op code to a saved list is Settings › Checklists' job, so admin/office only (the rules say so too).
const canEditTemplates = () => { try { return /admin|office/i.test(localStorage.getItem('currentUserRole') || ''); } catch { return false; } };
const otherLists = op => C.otherTemplates(op.OpCode, D.getTemplates());
const matches = (o, q) => !q || `${o.name} ${o.codes.join(' ')}`.toLowerCase().includes(q.toLowerCase());

let configured = false;
function configure() {
  if (configured) return;
  configured = true;
  D.configureChecklists({
    by: () => currentUserName() || 'Office',
    mirror: mirrorOp,
    onSaveError: () => toastMessage("Couldn't save the checklist. Check the connection.", 'red'),
  });
  D.watchChecklistSettings(() => { if (view.op && view.container?.isConnected) draw(); });
}

// The panel's copy of the op and this device's copy (IndexedDB), so a redraw or a reopen shows the
// change before the change feed brings the server's version back.
function mirrorOp(opId, updates) {
  if (view.op && (view.op.Job_ID || view.op.id) === opId) C.applyPaths(view.op, updates);
  getDB().then((db) => {
    const tx = db.transaction('jobData', 'readwrite');
    const store = tx.objectStore('jobData');
    const req = store.get(opId);
    req.onsuccess = () => { if (req.result) store.put(C.applyPaths(req.result, updates)); };
    tx.oncomplete = () => updateTimestamps('jobData', 'data');
  }).catch(err => console.error('Checklist: local copy not updated', err));
}

const initials = name => String(name || '').split(/\s+/).filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase();
const clock = at => (at ? new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '') : '');
const day = at => (at ? new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

function sourceText(cl) {
  const s = cl?.Source || {};
  if (s.kind === 'boat') return `This boat's list${s.name ? ` · ${s.name}` : ''}`;
  if (s.kind === 'template') return s.name || 'Saved checklist';
  if (s.kind === 'none') return 'No checklist';
  return 'Made for this operation';
}

/** Draws (or redraws) the op panel's Checklist section. */
export function renderOfficeChecklist(container, op, boat) {
  if (!container || !op) return;
  configure();
  const id = op.Job_ID || op.id;
  if (ui.opId !== id) Object.assign(ui, { opId: id, adding: false, changing: false, removing: null, find: '' });
  Object.assign(view, { container, op, boat });
  D.ensureList(op, boat).then((changed) => { if (changed && ui.opId === id) draw(); });
  draw();
}

function cellHTML(cl, cell) {
  if (!cell) return '<span class="op-cl-cell op-cl-cell--na"></span>';
  const d = cl.Done?.[cell.iid]?.[cell.key];
  const title = d ? `${d.By || ''} · ${day(d.At)} ${clock(d.At)}`.trim() : 'Not done yet';
  if (!C.isDone(cl, cell.iid, cell.key)) return `<span class="op-cl-cell op-cl-cell--open" title="${esc(title)}">—</span>`;
  if (cell.item.Kind === 'reading') return `<span class="op-cl-cell op-cl-cell--value" title="${esc(title)}">${esc(d.Value)}<small>${esc(cell.item.Unit || '')}</small></span>`;
  return `<span class="op-cl-cell op-cl-cell--done" title="${esc(title)}">✓ ${esc(initials(d.By))} ${esc(clock(d.At))}</span>`;
}

function draw() {
  const { container, op, boat } = view;
  if (!container || !op) return;
  const cl = op.Checklist || null;
  const code = C.normCode(op.OpCode);
  const options = D.listOptions(op, boat);
  const live = C.liveItems(cl);
  const removed = C.removedItems(cl);
  const p = C.progress(cl, boat);
  const tabs = p.tabs.filter(t => p.byTab[t.key].total > 0);
  const cells = C.cellsOf(cl, boat);
  const others = otherLists(op);
  const canKeep = !!(code && op.Boat_ID);
  const canAddCode = !!(C.templateCode(code) && canEditTemplates());
  const finished = ['Ready to Bill', 'Complete', 'Closed'].includes(String(op.Status || ''));

  const head = `
    <div class="op-cl-head">
      <div class="op-cl-heading">
        <div class="op-cl-title">Checklist${p.total ? ` <span class="op-cl-count op-cl-count--${C.meter(cl, boat, op.Status).state}">${p.done} of ${p.total} done</span>` : ''}</div>
        <div class="op-cl-source" title="${esc(cl?.Set_By ? `Set by ${cl.Set_By}` : '')}">${esc(cl ? sourceText(cl) : (code ? 'No checklist yet' : 'No op code, so no saved list'))}</div>
      </div>
      <div class="op-cl-actions">
        ${code || options.length || others.length ? `<button type="button" class="op-cl-btn" data-act="change">Change list</button>` : ''}
        <button type="button" class="op-cl-btn op-cl-btn--primary" data-act="add">+ Add item</button>
      </div>
    </div>`;

  const change = ui.changing ? `
    <div class="op-cl-form" data-key="change">
      <div class="op-cl-form-title">Put another list on this operation</div>
      <div class="op-cl-options">
        ${options.map(o => {
          const cur = cl?.Source?.kind === o.kind && cl?.Source?.id === o.id;
          return `<button type="button" class="op-cl-option${cur ? ' is-current' : ''}" data-act="pick" data-kind="${o.kind}" data-id="${esc(o.id)}">
            <b>${esc(o.kind === 'boat' ? `This boat's list${o.name ? ` · ${o.name}` : ''}` : o.name)}</b>
            <span>${o.count} item${o.count === 1 ? '' : 's'}${o.seasonOk ? '' : ' · other season'}${cur ? ' · in use' : ''}</span></button>`;
        }).join('')}
        ${live.length ? '<button type="button" class="op-cl-option op-cl-option--off" data-act="pick" data-kind="none"><b>No checklist</b><span>Take the list off this operation</span></button>' : ''}
        ${!options.length ? `<div class="op-cl-muted">${others.length ? `No saved list is made for ${code ? esc(code) : 'this operation'} yet. Pick one below.` : 'No saved lists yet. Make one in Settings › Checklists.'}</div>` : ''}
      </div>
      ${others.length ? `
        <div class="op-cl-form-sub">
          <span>Other saved lists</span>
          ${others.length > 6 ? `<input type="search" class="op-cl-input op-cl-find" data-f="find" placeholder="Find a list" value="${esc(ui.find)}">` : ''}
        </div>
        <div class="op-cl-options op-cl-options--other">
          ${others.map(o => `<button type="button" class="op-cl-option" data-act="pick" data-kind="other" data-id="${esc(o.id)}"${matches(o, ui.find) ? '' : ' hidden'}>
            <b>${esc(o.name)}</b>
            <span>${o.count} item${o.count === 1 ? '' : 's'}${o.codes.length ? ` · for ${esc(o.codes.join(', '))}` : ''}</span></button>`).join('')}
        </div>
        ${canAddCode ? `<label class="op-cl-keep"><input type="checkbox" data-f="addCode" ${ui.addCode !== false ? 'checked' : ''}> Offer the list I pick for ${esc(code)} from now on</label>` : ''}` : ''}
      ${canKeep ? `<label class="op-cl-keep"><input type="checkbox" data-f="changeKeep" ${ui.changeKeep !== false ? 'checked' : ''}> Remember this for the boat next time</label>` : ''}
      <div class="op-cl-form-foot"><button type="button" class="op-cl-btn" data-act="cancel">Cancel</button></div>
    </div>` : '';

  const add = ui.adding ? `
    <div class="op-cl-form" data-key="add">
      <div class="op-cl-form-title">Add an item the tech will see</div>
      <div class="op-cl-add-row">
        <input type="text" class="op-cl-input" data-f="name" maxlength="120" placeholder="What to do, e.g. Check trim tab anodes" value="${esc(ui.draft.name)}">
        <select class="op-cl-input op-cl-input--narrow" data-f="kind"><option value="check"${ui.draft.kind === 'check' ? ' selected' : ''}>Check</option><option value="reading"${ui.draft.kind === 'reading' ? ' selected' : ''}>Reading</option></select>
        ${ui.draft.kind === 'reading' ? `<select class="op-cl-input op-cl-input--unit" data-f="unit">${C.UNITS.map(u => `<option${u === ui.draft.unit ? ' selected' : ''}>${esc(u)}</option>`).join('')}</select>` : ''}
        <select class="op-cl-input op-cl-input--narrow" data-f="scope"><option value="engine"${ui.draft.scope === 'engine' ? ' selected' : ''}>Each engine</option><option value="boat"${ui.draft.scope === 'boat' ? ' selected' : ''}>Once per boat</option></select>
      </div>
      ${canKeep ? `<label class="op-cl-keep"><input type="checkbox" data-f="keep" ${ui.draft.keep ? 'checked' : ''}> Keep it on this boat's list for next time</label>` : ''}
      <div class="op-cl-form-foot">
        <button type="button" class="op-cl-btn" data-act="cancel">Cancel</button>
        <button type="button" class="op-cl-btn op-cl-btn--primary" data-act="save-add" ${ui.draft.name.trim() ? '' : 'disabled'}>Add item</button>
      </div>
    </div>` : '';

  let body = '';
  if (live.length || removed.length) {
    // Two groups, each with its own header: steps done on every engine (a column per engine) and
    // steps done once for the boat (one column, under the first engine's).
    const engineTabs = tabs.filter(t => t.key !== C.BOAT_KEY);
    const nCols = Math.max(engineTabs.length, 1);
    const cols = `minmax(180px, 1fr) repeat(${nCols}, minmax(78px, 104px)) 28px`;
    const onEngines = item => cells.some(c => c.iid === item.iid && c.key !== C.BOAT_KEY);
    const row = (item, keys) => {
      const mine = cells.filter(c => c.iid === item.iid);
      const isRemoving = ui.removing === item.iid;
      const meta = [
        item.Kind === 'reading' ? `<span class="op-cl-meta">reading${item.Unit ? `, ${esc(item.Unit)}` : ''}</span>` : '',
        item.Added_At ? `<span class="op-cl-pill">added by ${esc(item.Added_By || '')}${item.Once ? ' · this op only' : ''}</span>` : '',
      ].join('');
      const stab = item.Stabilizer ? (() => {
        const st = C.stabilizerFor(boat, D.getStabilizers());
        return `<div class="op-cl-stab">${st.fuel ? `${esc(st.fuel)} · ${esc([st.name, st.dose].filter(Boolean).join(' · ') || 'no stabilizer set for this fuel')}` : 'No fuel type on the boat'}</div>`;
      })() : '';
      const removeForm = isRemoving ? `<div class="op-cl-remove-form">
          Take “${esc(item.Name)}” off this operation?
          ${canKeep && !item.Once ? `<label class="op-cl-keep"><input type="checkbox" data-f="removeKeep" checked> Also off this boat's list</label>` : ''}
          <button type="button" class="op-cl-btn op-cl-btn--danger" data-act="confirm-remove" data-iid="${esc(item.iid)}">Take it off</button>
          <button type="button" class="op-cl-btn" data-act="cancel">Cancel</button></div>` : '';
      const slots = Array.from({ length: nCols }, (_, i) => (keys[i] ? cellHTML(cl, mine.find(c => c.key === keys[i])) : '<span></span>'));
      return `<div class="op-cl-row${isRemoving ? ' is-removing' : ''}" data-key="i-${esc(item.iid)}" style="grid-template-columns:${cols}">
          <div class="op-cl-item"><div class="op-cl-item-line"><span class="op-cl-item-name">${esc(item.Name)}</span>${meta}</div>${stab}</div>
          ${slots.join('')}
          <button type="button" class="op-cl-x" data-act="remove" data-iid="${esc(item.iid)}" title="Take off this operation" aria-label="Take ${esc(item.Name)} off">×</button>
        </div>${removeForm}`;
    };
    const engineItems = live.filter(onEngines);
    const boatItems = live.filter(it => !onEngines(it));
    const group = (title, heads, items, keys, key) => (items.length ? `
        <div class="op-cl-row op-cl-row--head" data-key="${key}" style="grid-template-columns:${cols}">
          <span>${esc(title)}</span>${Array.from({ length: nCols }, (_, i) => (heads[i] ? `<span title="${esc(heads[i].title)}">${esc(heads[i].label)}</span>` : '<span></span>')).join('')}<span></span>
        </div>${items.map(it => row(it, keys)).join('')}` : '');
    const rows = group('Each engine', engineTabs, engineItems, engineTabs.map(t => t.key), 'h-engines')
      + group(engineItems.length ? 'Once for the boat' : 'Item', [], boatItems, [C.BOAT_KEY], 'h-boat');
    const gone = removed.map(it => `<div class="op-cl-row op-cl-row--removed" data-key="x-${esc(it.iid)}" style="grid-template-columns:1fr auto">
        <div class="op-cl-item"><span class="op-cl-item-name">${esc(it.Name)}</span><span class="op-cl-pill op-cl-pill--off">taken off by ${esc(it.Removed_By || '')}${it.Removed_Once ? ' · this op only' : ''}</span></div>
        <button type="button" class="op-cl-btn" data-act="restore" data-iid="${esc(it.iid)}">Put back</button></div>`).join('');
    body = `
      <div class="op-cl-table">
        ${rows}${gone}
      </div>`;
  } else {
    body = `<div class="op-cl-muted op-cl-empty">${C.isCleared(cl) ? 'The list was taken off this operation. It won’t be put back on by itself.' : 'Nothing to tick on this operation yet.'}</div>`;
  }

  const reason = cl?.Open_Reason ? `<div class="op-cl-reason"><b>${finished && p.open ? `Finished with ${p.open} item${p.open === 1 ? '' : 's'} open.` : 'Why items are open:'}</b> ${esc(cl.Open_Reason)}</div>`
    : (finished && p.open ? `<div class="op-cl-reason"><b>Finished with ${p.open} item${p.open === 1 ? '' : 's'} open.</b> No reason given.</div>` : '');

  patchHTML(container, `<section class="op-cl" aria-label="Checklist">${head}${change}${add}${reason}${body}</section>`);
  wire(container);
}

function wire(container) {
  const { op, boat } = view;
  container.onclick = (e) => {
    const el = e.target.closest('[data-act]');
    if (!el || !container.contains(el)) return;
    const { act, iid, kind, id } = el.dataset;
    if (act === 'add') Object.assign(ui, { adding: !ui.adding, changing: false, removing: null });
    else if (act === 'change') Object.assign(ui, { changing: !ui.changing, adding: false, removing: null });
    else if (act === 'cancel') Object.assign(ui, { adding: false, changing: false, removing: null });
    else if (act === 'remove') ui.removing = ui.removing === iid ? null : iid;
    else if (act === 'confirm-remove') {
      const keep = container.querySelector('[data-f="removeKeep"]')?.checked ?? false;
      D.removeItem(op, iid, { keep });
      ui.removing = null;
    } else if (act === 'restore') D.restoreItem(op, iid);
    else if (act === 'save-add') {
      const name = ui.draft.name.trim();
      if (!name) return;
      D.addItem(op, { name, kind: ui.draft.kind, unit: ui.draft.kind === 'reading' ? ui.draft.unit : '', scope: ui.draft.scope, keep: !!(C.normCode(op.OpCode) && op.Boat_ID) && ui.draft.keep });
      ui.draft = { ...ui.draft, name: '' };
      ui.adding = false;
      toastMessage(`Added “${name}”: the tech sees it on the op`, 'green');
    } else if (act === 'pick') {
      const other = kind === 'other' ? otherLists(op).find(o => o.id === id) : null;
      const opt = kind === 'none' ? null : (other || D.listOptions(op, boat).find(o => o.kind === kind && o.id === id));
      if (kind !== 'none' && !opt) return;
      const ticked = Object.keys(op.Checklist?.Done || {}).length > 0;
      if (ticked && !confirm('The ticks and readings on the current list will be cleared. Switch lists?')) return;
      const keep = container.querySelector('[data-f="changeKeep"]')?.checked ?? false;
      const code = C.templateCode(op.OpCode);
      const addCode = !!(other && code && canEditTemplates() && (container.querySelector('[data-f="addCode"]')?.checked ?? false));
      D.switchList(op, opt, { keep, boat });
      ui.changing = false;
      if (addCode) {
        D.addTemplateCode(other.id, code).catch((err) => {
          console.error('Checklist: op code not added to the saved list', err);
          toastMessage(`Couldn't add ${code} to “${other.name}”`, 'red');
        });
      }
      toastMessage(opt ? `Checklist: ${opt.kind === 'boat' ? "this boat's list" : opt.name}${addCode ? ` · now offered for ${code}` : ''}` : 'Checklist taken off', 'green');
    } else return;
    draw();
  };
  container.oninput = (e) => {
    const f = e.target.dataset?.f;
    if (f === 'name') {
      ui.draft.name = e.target.value;
      const save = container.querySelector('[data-act="save-add"]');
      if (save) save.disabled = !ui.draft.name.trim();
    } else if (f === 'find') {
      // Filter in place: a redraw would replace the box being typed in.
      ui.find = e.target.value;
      const byId = new Map(otherLists(view.op).map(o => [o.id, o]));
      container.querySelectorAll('.op-cl-options--other [data-id]').forEach((b) => { b.hidden = !matches(byId.get(b.dataset.id) || { name: '', codes: [] }, ui.find); });
    }
  };
  container.onchange = (e) => {
    const f = e.target.dataset?.f;
    if (f === 'kind' || f === 'unit' || f === 'scope') { ui.draft[f] = e.target.value; draw(); }
    else if (f === 'keep') ui.draft.keep = e.target.checked;
    else if (f === 'changeKeep') ui.changeKeep = e.target.checked;
    else if (f === 'addCode') ui.addCode = e.target.checked;
  };
  container.onkeydown = (e) => {
    if (e.key === 'Enter' && e.target.dataset?.f === 'name') container.querySelector('[data-act="save-add"]')?.click();
  };
}

/** Is a form in the section open (the panel's live refresh waits for it)? */
export const officeChecklistBusy = () => !!(ui.adding || ui.changing || ui.removing);

/** The meter under an op row's title: "13 of 19 · checklist", amber when finished with items open. */
export function opRowChecklistHtml(op) {
  const cl = op?.Checklist;
  if (!cl || !C.liveItems(cl).length) return '';
  const m = C.meter(cl, null, op.Status);
  const pct = m.total ? Math.round((m.done / m.total) * 100) : 0;
  const text = m.state === 'open-finished' ? `${m.open} item${m.open === 1 ? '' : 's'} open` : `${m.done} of ${m.total}`;
  return `<div class="op-row-checklist op-row-checklist--${m.state}" title="Checklist: ${m.done} of ${m.total} done${cl.Open_Reason ? ` · ${esc(cl.Open_Reason)}` : ''}">
    <span class="op-row-checklist-bar"><span style="width:${pct}%"></span></span><span class="op-row-checklist-text">${esc(text)}</span></div>`;
}
